/**
 * Stock Movements Storage Service
 * Handles tracking and storage of stock movements for audit and transparency
 */

// LocalStorage key for stock movements
const STOCK_MOVEMENTS_KEY = 'qawaem-stock-movements';

// Get stock movements from localStorage
const getStoredMovements = () => {
  try {
    const stored = localStorage.getItem(STOCK_MOVEMENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading stock movements from localStorage:', error);
    return [];
  }
};

// Save stock movements to localStorage
const saveMovements = (movements) => {
  try {
    localStorage.setItem(STOCK_MOVEMENTS_KEY, JSON.stringify(movements));
  } catch (error) {
    console.error('Error saving stock movements to localStorage:', error);
  }
};

// Initialize stock movements from localStorage
let stockMovements = getStoredMovements();

/**
 * Add a stock movement record
 * @param {Object} movement - Stock movement data
 */
export const addStockMovement = (movement) => {
  const movementWithId = {
    ...movement,
    id: Date.now() + Math.random(), // Simple ID generation
    timestamp: movement.timestamp || new Date().toISOString()
  };
  
  stockMovements.unshift(movementWithId); // Add to beginning for latest first
  
  // Keep only last 1000 movements to prevent memory issues
  if (stockMovements.length > 1000) {
    stockMovements = stockMovements.slice(0, 1000);
  }
  
  // Save to localStorage
  saveMovements(stockMovements);
  
  console.log('📦 Stock movement recorded:', movementWithId);
  return movementWithId;
};

/**
 * Get all stock movements with optional filtering
 * @param {Object} filters - Optional filters
 * @returns {Array} - Filtered stock movements
 */
export const getStockMovements = (filters = {}) => {
  let filtered = [...stockMovements];
  
  if (filters.productId) {
    filtered = filtered.filter(m => m.productId === filters.productId);
  }
  
  if (filters.documentType) {
    filtered = filtered.filter(m => m.documentType === filters.documentType);
  }
  
  if (filters.operationType) {
    filtered = filtered.filter(m => m.operationType === filters.operationType);
  }
  
  if (filters.limit) {
    filtered = filtered.slice(0, filters.limit);
  }
  
  return filtered;
};

/**
 * Get stock movements for a specific product
 * @param {string} productId - Product ID
 * @param {number} limit - Optional limit
 * @returns {Array} - Product stock movements
 */
export const getProductStockMovements = (productId, limit = 50) => {
  return getStockMovements({ productId, limit });
};

/**
 * Get stock movements for a specific document
 * @param {string} documentId - Document ID
 * @returns {Array} - Document stock movements
 */
export const getDocumentStockMovements = (documentId) => {
  return stockMovements.filter(m => m.documentId === documentId);
};

/**
 * Get recent stock movements
 * @param {number} limit - Number of movements to return
 * @returns {Array} - Recent stock movements
 */
export const getRecentStockMovements = (limit = 20) => {
  return stockMovements.slice(0, limit);
};

/**
 * Clear all stock movements (for testing or reset)
 */
export const clearStockMovements = () => {
  stockMovements = [];
  // Clear from localStorage as well
  saveMovements(stockMovements);
  console.log('📦 Stock movements cleared');
};

/**
 * Get stock movements statistics
 * @returns {Object} - Statistics about stock movements
 */
export const getStockMovementsStats = () => {
  const total = stockMovements.length;
  const increases = stockMovements.filter(m => m.operationType === 'INCREASE').length;
  const decreases = stockMovements.filter(m => m.operationType === 'DECREASE').length;
  
  // Get unique products affected
  const uniqueProducts = new Set(stockMovements.map(m => m.productId)).size;
  
  // Get unique document types
  const uniqueDocumentTypes = new Set(stockMovements.map(m => m.documentType)).size;
  
  return {
    total,
    increases,
    decreases,
    uniqueProducts,
    uniqueDocumentTypes,
    lastMovement: stockMovements[0]?.timestamp || null
  };
};

/**
 * Export stock movements to JSON (for backup/analysis)
 * @returns {string} - JSON string of stock movements
 */
export const exportStockMovements = () => {
  return JSON.stringify({
    exportDate: new Date().toISOString(),
    movementsCount: stockMovements.length,
    movements: stockMovements
  }, null, 2);
};

/**
 * Import stock movements from JSON
 * @param {string} jsonData - JSON string of stock movements
 * @returns {number} - Number of movements imported
 */
export const importStockMovements = (jsonData) => {
  try {
    const data = JSON.parse(jsonData);
    if (data.movements && Array.isArray(data.movements)) {
      stockMovements = data.movements;
      // Save imported data to localStorage
      saveMovements(stockMovements);
      console.log(`📦 Imported ${stockMovements.length} stock movements`);
      return stockMovements.length;
    }
  } catch (error) {
    console.error('📦 Error importing stock movements:', error);
  }
  return 0;
};

export default {
  addStockMovement,
  getStockMovements,
  getProductStockMovements,
  getDocumentStockMovements,
  getRecentStockMovements,
  clearStockMovements,
  getStockMovementsStats,
  exportStockMovements,
  importStockMovements
};

